import { Element, useEditor, useNode } from "@craftjs/core";
import { useContext } from "react";
import { Actions } from "src/types";
import { ThemeContext } from "styled-components";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Button from "@/components/editor/primitives/button/button-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import BackgroundSquiggle from "@/components/editor/shared/background-squiggle";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import { blackLogo, whiteLogo } from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import {
  ButtonWrapper,
  Container,
  ContentWrapper,
  CTATextWrapper,
  HeadingWrapper,
  Image,
  ImageWrapper,
  LeftColumn,
  Logo,
  LogoWrapper,
  RightColumn,
  SubheadingWrapper,
} from "@/components/editor/themes/crosby/blocks/headers/header-option-3/header-option-3-component-styles";
import {
  ColorLabels,
  useInvertedColor,
} from "@/components/editor/utils/use-color";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";

import { DEFAULT_BUTTON_TEXT, DEFAULT_SUBHEADING_TEXT } from "./constants";

type HeaderOption3Props = {
  backgroundImage?: string;
  backgroundImageAlt?: string;
  backgroundPosition?: ImagePosition;
  ctaText: string;
  heading: string;
  isLogoShowing?: boolean;
  logo?: string;
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => {
  return (
    <HeaderSettings
      optimalImageSize="1440x360px"
      backgroundImageSize={{
        width: "288",
        height: "72",
      }}
    />
  );
};

export default function HeaderOption3(props: HeaderOption3Props) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const theme = useContext(ThemeContext);

  const showLightLogo =
    useInvertedColor(theme.colors[ColorLabels.BACKGROUND]) === "#ffffff";

  const defaultLogoImage = showLightLogo ? whiteLogo : blackLogo;

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "heading",
    "cta-text",
    "subheading",
  ]);

  return (
    <BlockBackgroundColorWrapper
      ref={connect}
      id={nodeId}
      className={!enabled ? "disable-scroll" : undefined}
    >
      <ImageWrapper>
        <Image
          src={props.backgroundImage}
          alt={props.backgroundImageAlt || ""}
          $position={props.backgroundPosition || "center center"}
          imageFit={props.imageFit}
        />
      </ImageWrapper>
      <BlockMaxWidthWrapper>
        <Container>
          {props.isLogoShowing && (
            <LogoWrapper>
              <Logo
                draggable="false"
                data-testid="header-option-3-logo"
                src={props.logo || defaultLogoImage}
                style={{
                  width: formatLogoSize(props.logoSize),
                  height: "auto",
                }}
                isLogoResized={!isNaN(props.logoSize)}
                alt={"Header Logo"}
              />
            </LogoWrapper>
          )}
          <ContentWrapper isLogoShowing={props.isLogoShowing}>
            <BackgroundSquiggle />
            <LeftColumn>
              {isRichTextCompatible ? (
                <>
                  <HeadingWrapper>
                    <Element
                      id="heading"
                      is={RichText}
                      text={`<h2>${props.heading}</h2>`}
                    />
                  </HeadingWrapper>
                  <CTATextWrapper>
                    <Element
                      id="cta-text"
                      is={RichText}
                      text={`<h5>${props.ctaText}</h5>`}
                    />
                  </CTATextWrapper>
                </>
              ) : (
                <>
                  <HeadingWrapper>
                    <Element
                      id="heading"
                      is={Text}
                      element="h1"
                      text={props.heading}
                      pattern="headingOne"
                    />
                  </HeadingWrapper>
                  <CTATextWrapper>
                    <Element
                      id="cta-text"
                      is={Text}
                      text={props.ctaText}
                      pattern="headingFour"
                    />
                  </CTATextWrapper>
                </>
              )}
            </LeftColumn>
            <RightColumn>
              <SubheadingWrapper>
                {isRichTextCompatible ? (
                  <>
                    <Element
                      id="subheading"
                      is={RichText}
                      text={`<p>${DEFAULT_SUBHEADING_TEXT}</p>`}
                    />
                  </>
                ) : (
                  <>
                    <Element
                      id="subheading"
                      is={Text}
                      text={DEFAULT_SUBHEADING_TEXT}
                      pattern="paragraph"
                    />
                  </>
                )}
              </SubheadingWrapper>
              <ButtonWrapper>
                <Element
                  id="cta-button"
                  is={Button}
                  text={DEFAULT_BUTTON_TEXT}
                  action={{
                    kind: Actions.TicketSelector,
                  }}
                />
              </ButtonWrapper>
            </RightColumn>
          </ContentWrapper>
        </Container>
      </BlockMaxWidthWrapper>
    </BlockBackgroundColorWrapper>
  );
}

HeaderOption3.craft = {
  custom: {
    displayName: "Header option #3",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
